import React from 'react';
import { NavLink } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { cn } from '../utils/cn';

const navItems = [
  { path: '/', icon: FiIcons.FiGrid, label: 'Dashboard' },
  { path: '/analytics', icon: FiIcons.FiActivity, label: 'Analytics' },
  { path: '/trades', icon: FiIcons.FiClock, label: 'History' },
  { path: '/trades-list', icon: FiIcons.FiList, label: 'Trades List' },
  { path: '/wallet', icon: FiIcons.FiShield, label: 'Burner Wallet' },
  { path: '/settings', icon: FiIcons.FiSettings, label: 'Settings' },
];

export default function Sidebar({ isOpen, onClose, solPrice }) {
  const change = solPrice?.change24h;
  const up = typeof change === 'number' && change >= 0;
  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden" onClick={onClose} />
      )}

      {/* Sidebar Container */}
      <aside className={cn(
        "fixed lg:static inset-y-0 left-0 z-50 w-64 bg-[#0B0E14] border-r border-slate-800/50 flex flex-col transition-transform duration-300 ease-in-out",
        isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
      )}>
        
        {/* Logo Area */}
        <div className="h-16 flex items-center gap-3 px-6 border-b border-slate-800/50 flex-shrink-0">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center shadow-lg shadow-purple-500/20">
            <SafeIcon icon={FiIcons.FiActivity} className="text-white text-lg" />
          </div>
          <div>
            <h1 className="text-lg font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-slate-400 tracking-tight">
              SolSnipe
            </h1>
          </div>
        </div>

        {/* Navigation Links */}
        <nav className="flex-1 py-6 px-3 space-y-1 overflow-y-auto custom-scrollbar">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-widest px-3 mb-4">Menu</div>
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={() => onClose()}
              className={({ isActive }) => cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200",
                isActive 
                  ? "bg-purple-500/10 text-purple-400 border border-purple-500/20" 
                  : "text-slate-400 border border-transparent hover:text-slate-200 hover:bg-slate-800/50"
              )}
            >
              <SafeIcon icon={item.icon} className="text-lg flex-shrink-0" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* Bottom Status Area */}
        <div className="p-4 border-t border-slate-800/50 flex-shrink-0 space-y-3">
          {/* SOL price + 24h change */}
          <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-800">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center flex-shrink-0">
                  <span className="text-[10px] font-bold text-white">S</span>
                </div>
                <div className="text-sm font-bold text-slate-200">SOL</div>
              </div>
              <div className="text-right">
                <div className="text-sm font-bold text-white tabular-nums">
                  {solPrice?.price != null
                    ? `$${solPrice.price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                    : '—'}
                </div>
                {typeof change === 'number' && (
                  <div className={cn(
                    "flex items-center justify-end gap-1 text-[11px] font-semibold tabular-nums",
                    up ? "text-green-400" : "text-red-400"
                  )}>
                    <SafeIcon icon={up ? FiIcons.FiTrendingUp : FiIcons.FiTrendingDown} className="text-[11px]" />
                    {`${up ? '+' : ''}${change.toFixed(2)}%`}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Network status */}
          <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-800">
            <div className="text-xs text-slate-400 mb-2">Network Status</div>
            <div className="flex items-center gap-2 text-sm font-medium text-green-400">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              Mainnet Beta
            </div>
          </div>
        </div>

      </aside>
    </>
  );
}